/**
 * HelpSurface (WEB) — centered popover/dialog presentation of a contextual help entry (WO-H1).
 * Renders the SAME HelpSurfaceContent as the native bottom sheet, so help content stays at
 * full parity across platforms; only the container differs. Esc-to-close + scrim-click-to-close;
 * width-capped for desktop readability.
 */
import React, { useEffect } from 'react';
import { Modal, Pressable, View, StyleSheet, ScrollView } from 'react-native';
import { theme } from '../../theme';
import { HelpSurfaceContent, type HelpSurfaceProps } from './HelpSurfaceContent';

export function HelpSurface({ visible, onClose, ...rest }: HelpSurfaceProps) {
  // Desktop affordance: Escape closes the dialog.
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (typeof window !== 'undefined') window.addEventListener('keydown', onKey);
    return () => {
      if (typeof window !== 'undefined') window.removeEventListener('keydown', onKey);
    };
  }, [visible, onClose]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.root}>
        <Pressable style={styles.scrim} onPress={onClose} accessibilityLabel="Close help" />
        <View style={styles.dialogWrap} accessibilityViewIsModal accessibilityRole="none">
          <ScrollView bounces={false} contentContainerStyle={styles.scroll} style={styles.dialog}>
            <HelpSurfaceContent onClose={onClose} {...rest} />
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: theme.spacing.lg },
  scrim: { ...StyleSheet.absoluteFillObject, backgroundColor: theme.color.scrim },
  dialogWrap: { width: '100%', maxWidth: 460, maxHeight: '85%' },
  dialog: { width: '100%' },
  scroll: { paddingBottom: 0 },
});
